<template>
    <AdminLayout>
        <template #content>
            <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h1 class="h4 mb-1">Détail de la vente</h1>
                    <div v-if="sale">
                        <span class="me-2">Référence :</span>
                        <strong>{{ sale.reference }}</strong>
                        <span class="badge ms-2" :class="statusClass(sale.status)">
                            {{ statusLabel(sale.status) }}
                        </span>
                    </div>
                </div>

                <div class="d-flex flex-wrap justify-content-end gap-2">
                    <button class="btn btn-outline-secondary btn-sm btn-icon-label" title="Retour à la liste" @click="goBack">
                        <i class="fa fa-arrow-left"></i>
                        <span class="btn-label ms-1">Retour à la liste</span>
                    </button>
                    <button class="btn btn-success btn-sm btn-icon-label" @click="validateCurrentSale" :disabled="!sale || sale.status !== 'draft'"
                        v-if="canValidate">
                        <i class="fa fa-check"></i>
                        <span class="btn-label ms-1">Valider</span>
                    </button>

                    <button class="btn btn-warning btn-sm btn-icon-label" @click="cancelCurrentSale" :disabled="!sale || sale.status !== 'validated'"
                        v-if="canCancel">
                        <i class="fa fa-ban"></i>
                        <span class="btn-label ms-1">Annuler</span>
                    </button>

                    <AppLoadingButton class="btn btn-danger btn-sm btn-icon-label" @click="deleteCurrentSale" :loading="isDeleting" :disabled="!sale || sale.status !== 'draft'" loading-label="Suppression..."
                        v-if="canDelete">
                        <i class="fa fa-trash"></i>
                        <span class="btn-label ms-1">Supprimer</span>
                    </AppLoadingButton>

                    <button class="btn btn-outline-primary btn-sm btn-icon-label" title="Aperçu du ticket" @click="ticketDialogVisible = true" :disabled="!sale">
                        <i class="fa fa-receipt"></i>
                        <span class="btn-label ms-1">Aperçu du ticket</span>
                    </button>

                    <AppLoadingButton
                        v-if="sale?.status === 'validated'"
                        class="btn btn-primary btn-sm btn-icon-label"
                        title="Imprimer facture"
                        :loading="isPreparingDocumentPrint && pendingDocumentType === 'invoice'"
                        :disabled="!sale || isPreparingDocumentPrint"
                        loading-label="Préparation..."
                        @click="printSaleDocument('invoice')"
                    >
                        <i class="fa fa-file-invoice"></i>
                        <span class="btn-label ms-1">Imprimer facture</span>
                    </AppLoadingButton>

                    <AppLoadingButton
                        v-if="sale?.status === 'draft'"
                        class="btn btn-outline-primary btn-sm btn-icon-label"
                        title="Imprimer devis"
                        :loading="isPreparingDocumentPrint && pendingDocumentType === 'quote'"
                        :disabled="!sale || isPreparingDocumentPrint"
                        loading-label="Préparation..."
                        @click="printSaleDocument('quote')"
                    >
                        <i class="fa fa-file-lines"></i>
                        <span class="btn-label ms-1">Imprimer devis</span>
                    </AppLoadingButton>
                </div>
            </div>

            <!-- Messages -->
            <div v-if="isLoading" class="alert alert-info py-2">
                Chargement de la vente...
            </div>

            <div v-if="errorMessage" class="alert alert-danger py-2">
                {{ errorMessage }}
            </div>

            <div v-if="successMessage" class="alert alert-success py-2">
                {{ successMessage }}
            </div>

            <!-- Vente introuvable -->
            <div v-if="!isLoading && !sale" class="alert alert-danger">
                Vente introuvable.
            </div>

            <div v-else-if="sale" class="sale-detail-shell">
                <div class="row g-3 mb-3">
                    <div class="col-md-3">
                        <div class="sale-stat-card">
                            <div class="small text-muted">Total net</div>
                            <div class="h5 mb-0">{{ $format.currency(sale.total_amount || 0) }}</div>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="sale-stat-card">
                            <div class="small text-muted">Sous-total</div>
                            <div class="h5 mb-0">{{ $format.currency(sale.subtotal_amount || sale.total_amount || 0) }}</div>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="sale-stat-card">
                            <div class="small text-muted">Quantités actives</div>
                            <div class="h5 mb-0">{{ totalActiveQuantity }}</div>
                        </div>
                    </div>
                    <div class="col-md-3">
                        <div class="sale-stat-card">
                            <div class="small text-muted">Lignes</div>
                            <div class="h5 mb-0">{{ saleLines.length }}</div>
                        </div>
                    </div>
                </div>

                <!-- Infos principales -->
                <div class="row g-3 mb-3 align-items-start">
                    <div class="col-lg-4 col-md-6">
                        <div class="card border-0 shadow-sm">
                            <div class="card-header bg-white border-0 pb-1">
                                <h6 class="mb-0">Client</h6>
                            </div>
                            <div class="card-body pt-2">
                                <div class="detail-row">
                                    <span>Nom ticket</span>
                                    <strong>{{ sale.customer_name || 'comptant' }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Client lié</span>
                                    <strong>{{ sale.client?.name || '—' }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Téléphone</span>
                                    <strong>{{ sale.client?.phone || '—' }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Email</span>
                                    <strong>{{ sale.client?.email || '—' }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Adresse</span>
                                    <strong>{{ clientAddress }}</strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-lg-4 col-md-6">
                        <div class="card border-0 shadow-sm">
                            <div class="card-header bg-white border-0 pb-1">
                                <h6 class="mb-0">Paiement</h6>
                            </div>
                            <div class="card-body pt-2">
                                <div class="detail-row">
                                    <span>Mode</span>
                                    <strong>{{ paymentMethodLabel(sale.payment_method) }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Référence</span>
                                    <strong>{{ sale.payment_reference || '—' }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Payée le</span>
                                    <strong>{{ formatDateTime(sale.paid_at) }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Payée par</span>
                                    <strong>{{ userName(sale.payer) }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Reçu / rendu</span>
                                    <strong>{{ $format.currency(sale.amount_received || 0) }} / {{ $format.currency(sale.amount_returned || 0) }}</strong>
                                </div>
                                <div v-if="sale.payment_method === 'credit'" class="detail-row">
                                    <span>Crédit</span>
                                    <strong>
                                        Payé {{ $format.currency(sale.credit_paid_amount || 0) }} ·
                                        Reste {{ $format.currency(sale.credit_due_amount || 0) }}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-lg-4 col-md-6">
                        <div class="card border-0 shadow-sm">
                            <div class="card-header bg-white border-0 pb-1">
                                <h6 class="mb-0">Caisse et suivi</h6>
                            </div>
                            <div class="card-body pt-2">
                                <div class="detail-row">
                                    <span>Caisse</span>
                                    <strong>{{ cashRegisterLabel }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Session</span>
                                    <strong>{{ cashSessionLabel }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Validée le</span>
                                    <strong>{{ formatDateTime(sale.validated_at) }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Validée par</span>
                                    <strong>{{ userName(sale.validator) }}</strong>
                                </div>
                                <div class="detail-row">
                                    <span>Annulée par</span>
                                    <strong>{{ userName(sale.canceller) }}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Lignes de vente -->
                <section class="sale-lines-section mb-3">
                    <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                        <h5 class="mb-0">Lignes de la vente</h5>
                        <small v-if="saleLines.length" class="text-muted">{{ saleLines.length }} ligne(s)</small>
                    </div>

                    <BootstrapDataTable
                        :rows="saleLineTableRows"
                        :columns="saleLineColumns"
                        row-key="_row_key"
                        empty-text="Aucune ligne de vente enregistrée."
                        client-pagination
                        :per-page="10"
                        :show-refresh="false"
                        :show-export="false"
                        :show-print="false"
                    >
                        <template #cell-line_number="{ row }">
                            <span class="text-muted">{{ row.line_number }}</span>
                        </template>

                        <template #cell-product_name="{ row }">
                            <div class="sale-line-product">
                                <span
                                    class="sale-line-product-name"
                                    :title="row.product_name || 'Produit sans nom'"
                                >
                                    {{ truncateProductName(row.product_name) }}
                                </span>
                                <small class="text-muted sale-line-product-sku">{{ lineSku(row) }}</small>
                            </div>
                        </template>

                        <template #cell-current_stock="{ row }">
                            <div class="text-end">{{ row.current_stock ?? '—' }}</div>
                        </template>

                        <template #cell-unit_price="{ row }">
                            <div class="text-end">
                                <div v-if="isPromotionLine(row)" class="text-muted text-decoration-line-through small">
                                    {{ $format.currency(row.base_unit_price || row.unit_price || 0) }}
                                </div>
                                <div class="fw-semibold">{{ $format.currency(row.unit_price || 0) }}</div>
                                <span v-if="isPromotionLine(row)" class="badge bg-success">
                                    {{ row.price_source === 'cashier_promotion' ? 'Promo caisse' : 'Promo' }}
                                </span>
                                <span v-if="isFormulaLine(row)" class="badge bg-primary">
                                    Formule {{ row.formula_quantity }} pour {{ $format.currency(row.formula_price || 0) }}
                                </span>
                            </div>
                        </template>

                        <template #cell-sale_price_coefficient="{ row }">
                            <div class="text-end">
                                <span
                                    v-if="row.sale_price_coefficient !== null && row.sale_price_coefficient !== undefined"
                                    class="badge"
                                    :class="coefficientBadgeClass(row.sale_price_coefficient)"
                                >
                                    {{ coefficientLabel(row.sale_price_coefficient) }}
                                </span>
                                <span v-else class="text-muted">—</span>
                            </div>
                        </template>

                        <template #cell-quantity="{ row }">
                            <div class="text-end">{{ row.quantity }}</div>
                        </template>

                        <template #cell-active_quantity="{ row }">
                            <div class="text-end">{{ remainingQuantity(row) }}</div>
                        </template>

                        <template #cell-cancelled_quantity="{ row }">
                            <div class="text-end">
                                <span v-if="Number(row.cancelled_quantity || 0) > 0" class="badge bg-warning text-dark">
                                    {{ row.cancelled_quantity }}
                                </span>
                                <span v-else>—</span>
                            </div>
                        </template>

                        <template #cell-initial_total="{ row }">
                            <div class="text-end">{{ $format.currency(row.total_line || 0) }}</div>
                        </template>

                        <template #cell-active_total="{ row }">
                            <div class="text-end fw-semibold">{{ $format.currency(activeLineTotal(row)) }}</div>
                        </template>

                        <template #cell-taxes="{ row }">
                            <div class="small text-end">
                                <div v-if="Number(row.tva_amount || 0) > 0">TVA {{ $format.currency(row.tva_amount || 0) }}</div>
                                <div v-if="Number(row.css_amount || 0) > 0">CSS {{ $format.currency(row.css_amount || 0) }}</div>
                                <span v-if="Number(row.tax_amount || 0) <= 0" class="text-muted">—</span>
                            </div>
                        </template>

                        <template #cell-cancellation="{ row }">
                            <div v-if="Number(row.cancelled_quantity || 0) > 0" class="small">
                                <div>{{ formatDateTime(row.cancelled_at) }}</div>
                                <div class="text-muted">{{ userName(row.canceller) }}</div>
                                <div v-if="row.cancellation_reason" class="text-muted">{{ row.cancellation_reason }}</div>
                            </div>
                            <span v-else class="text-muted">—</span>
                        </template>

                        <template #cell-status="{ row }">
                            <span class="badge" :class="lineStatusClass(row)">
                                {{ lineStatusLabel(row) }}
                            </span>
                        </template>

                        <template #cell-actions="{ row }">
                            <button
                                v-if="canCancelLine(row)"
                                type="button"
                                class="btn btn-outline-warning btn-sm btn-action-icon"
                                title="Annuler ce produit"
                                @click.stop="openCancelLineDialog(row)"
                            >
                                <i class="fa fa-rotate-left"></i>
                            </button>
                        </template>
                    </BootstrapDataTable>

                    <div class="sale-lines-summary text-end mt-3">
                        <div>Sous-total : {{ $format.currency(sale.subtotal_amount || 0) }}</div>
                        <div>Remise : - {{ $format.currency(sale.discount_amount || 0) }}</div>
                        <div>TVA incluse : {{ $format.currency(sale.tva_amount || 0) }}</div>
                        <div>CSS incluse : {{ $format.currency(sale.css_amount || 0) }}</div>
                        <h5>Total net : {{ $format.currency(sale.total_amount) }}</h5>
                    </div>
                </section>
                <PrimevueDialog
                    v-model:visible="ticketDialogVisible"
                    :modal="true"
                    :style="{ width: '420px', maxWidth: '100%' }"
                    header="Aperçu du ticket"
                >
                    <div id="printable-ticket" v-if="sale" class="my-2">
                        <SaleTicket
                            :sale="sale"
                            :shop-info="shopInfo"
                            :cashierName="currentUser?.name || ''"
                        />
                    </div>

                    <template #footer>
                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="ticketDialogVisible = false">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Fermer
                        </button>
                        <button type="button" class="btn btn-primary btn-sm" @click="printTicket" :disabled="!sale">
                            <i class="fa fa-print me-1"></i>
                            Imprimer
                        </button>
                    </template>
                </PrimevueDialog>

                <PrimevueDialog
                    v-model:visible="saleDocumentDialogVisible"
                    :modal="true"
                    :style="{ width: '920px', maxWidth: '98vw' }"
                    :header="saleDocumentDialogTitle"
                >
                    <div id="printable-sale-document" v-if="saleDocumentPrintData" class="my-2">
                        <SaleDocumentPrint
                            :sale="saleDocumentPrintData.sale"
                            :shop-info="saleDocumentPrintData.shop"
                            :document="saleDocumentPrintData.document"
                            :qr-code-url="saleDocumentQrCode"
                        />
                    </div>

                    <template #footer>
                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="saleDocumentDialogVisible = false">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Fermer
                        </button>
                        <button
                            type="button"
                            class="btn btn-primary btn-sm"
                            @click="printPreparedSaleDocument"
                            :disabled="!saleDocumentPrintData"
                        >
                            <i class="fa fa-print me-1"></i>
                            Imprimer
                        </button>
                    </template>
                </PrimevueDialog>

                <PrimevueDialog
                    v-model:visible="cancelLineDialogVisible"
                    :modal="true"
                    :style="{ width: '440px', maxWidth: '100%' }"
                    header="Annuler un produit"
                >
                    <div v-if="selectedLine">
                        <div class="mb-2">
                            <div class="small text-muted">Produit</div>
                            <div class="fw-semibold">{{ selectedLine.product_name }}</div>
                        </div>
                        <div class="mb-3">
                            <label class="form-label small">Quantité à annuler</label>
                            <input
                                v-model.number="cancelLineForm.quantity"
                                type="number"
                                min="1"
                                :max="remainingQuantity(selectedLine)"
                                class="form-control"
                            />
                            <div class="form-text">
                                Quantité restante : {{ remainingQuantity(selectedLine) }}
                            </div>
                        </div>
                        <div class="mb-0">
                            <label class="form-label small">Motif</label>
                            <textarea
                                v-model="cancelLineForm.reason"
                                rows="3"
                                class="form-control"
                                placeholder="Motif optionnel"
                            ></textarea>
                        </div>
                    </div>

                    <template #footer>
                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeCancelLineDialog">
            <i class="fa fa-xmark me-1" aria-hidden="true"></i>
            Fermer
                        </button>
                        <button
                            type="button"
                            class="btn btn-warning btn-sm"
                            :disabled="isCancellingLine"
                            @click="submitCancelLine"
                        >
                            <i class="fa fa-rotate-left me-1"></i>
                            Annuler la ligne
                        </button>
                    </template>
                </PrimevueDialog>
            </div>
        </template>
    </AdminLayout>
</template>

<script>
import { defineComponent, computed, nextTick } from 'vue';
import QRCode from 'qrcode';
import { useStore } from 'vuex';
import SaleDocumentPrint from './SaleDocumentPrint.vue';
import SaleTicket from './SaleTicket.vue';
import { canBypassPermissions } from '@/support/permissions';

export default defineComponent({
    name: 'SaleShowView',
    components: {
        SaleDocumentPrint,
        SaleTicket
    },
    data() {
        return {
            sale: null,
            isLoading: false,
            errorMessage: '',
            successMessage: '',
            ticketDialogVisible: false,
            saleDocumentDialogVisible: false,
            saleDocumentPrintData: null,
            saleDocumentQrCode: '',
            isPreparingDocumentPrint: false,
            pendingDocumentType: '',
            cancelLineDialogVisible: false,
            selectedLine: null,
            cancelLineForm: {
                quantity: 1,
                reason: '',
            },
            isCancellingLine: false,
            isDeleting: false,
        };
    },
    setup() {
        const store = useStore();
        const currentUser = computed(() => store.getters['auth/currentUser']);
        const role = computed(() => currentUser.value?.role || '');

        const isCaissier = computed(() => role.value === 'caissier');
        const isGerant = computed(() => role.value === 'gerant');
        const canFinalizeClosedCashRegisterSale = computed(() => canBypassPermissions(currentUser.value));

        return {
            currentUser,
            role,
            isCaissier,
            isGerant,
            canFinalizeClosedCashRegisterSale,
        };
    },
    computed: {
        canValidate() {
            return this.canFinalizeDraftSale(this.sale);
        },
        canCancel() {
            // Seul le gérant peut annuler une vente validée
            return this.isGerant && this.sale?.status === 'validated';
        },
        canDelete() {
            // Suppression seulement si brouillon
            return (this.isCaissier || this.isGerant) && this.sale?.status === 'draft';
        },
        shopInfo() {
            return this.$store.getters['shop/shopInfo'] || {};
        },
        discountLabel() {
            if (!this.sale || !this.sale.discount_type || !Number(this.sale.discount_amount || 0)) {
                return this.$format.currency(0);
            }

            const amount = this.$format.currency(this.sale.discount_amount || 0);
            if (this.sale.discount_type === 'percent') {
                return `${amount} (${Number(this.sale.discount_value || 0)} %)`;
            }

            return amount;
        },
        saleLines() {
            return this.sale?.items || [];
        },
        saleLineTableRows() {
            return this.saleLines.map((item, index) => ({
                ...item,
                _row_key: item.id || `line-${index}`,
                line_number: index + 1,
                current_stock: item.product?.stock_quantity,
            }));
        },
        saleLineColumns() {
            return [
                {
                    key: 'line_number',
                    label: '#',
                    class: 'text-muted',
                    style: 'width: 56px; min-width: 56px;',
                },
                {
                    key: 'product_name',
                    label: 'Produit',
                    class: 'sale-line-product-cell',
                    style: 'width: 340px; min-width: 300px; max-width: 380px;',
                },
                {
                    key: 'current_stock',
                    label: 'Stock actuel',
                    class: 'text-end',
                    style: 'width: 120px; min-width: 120px;',
                },
                {
                    key: 'unit_price',
                    label: 'PU',
                    class: 'text-end',
                    style: 'width: 130px; min-width: 130px;',
                },
                {
                    key: 'sale_price_coefficient',
                    label: 'Coef.',
                    class: 'text-end',
                    style: 'width: 100px; min-width: 100px;',
                },
                {
                    key: 'quantity',
                    label: 'Qté vendue',
                    class: 'text-end',
                    style: 'width: 110px; min-width: 110px;',
                },
                {
                    key: 'active_quantity',
                    label: 'Qté active',
                    class: 'text-end',
                    style: 'width: 110px; min-width: 110px;',
                },
                {
                    key: 'cancelled_quantity',
                    label: 'Annulée',
                    class: 'text-end',
                    style: 'width: 110px; min-width: 110px;',
                },
                {
                    key: 'initial_total',
                    label: 'Total initial',
                    class: 'text-end',
                    style: 'width: 140px; min-width: 140px;',
                },
                {
                    key: 'active_total',
                    label: 'Total actif',
                    class: 'text-end',
                    style: 'width: 140px; min-width: 140px;',
                },
                {
                    key: 'taxes',
                    label: 'Taxes',
                    class: 'text-end',
                    style: 'width: 150px; min-width: 150px;',
                },
                {
                    key: 'cancellation',
                    label: 'Annulation',
                    style: 'width: 190px; min-width: 190px;',
                },
                {
                    key: 'status',
                    label: 'Statut',
                    style: 'width: 110px; min-width: 110px;',
                },
                {
                    key: 'actions',
                    label: '',
                    class: 'text-end',
                    style: 'width: 80px; min-width: 80px;',
                },
            ];
        },
        totalSoldQuantity() {
            return this.saleLines.reduce((total, item) => total + Number(item.quantity || 0), 0);
        },
        totalCancelledQuantity() {
            return this.saleLines.reduce((total, item) => total + Number(item.cancelled_quantity || 0), 0);
        },
        totalActiveQuantity() {
            return this.saleLines.reduce((total, item) => total + this.remainingQuantity(item), 0);
        },
        clientAddress() {
            const client = this.sale?.client;
            if (!client) return '—';

            return [client.address, client.city].filter(Boolean).join(', ') || '—';
        },
        cashRegisterLabel() {
            const session = this.sale?.cash_register_session;
            const register = this.sale?.cash_register || session?.cash_register;
            if (!register) return '—';

            return [register.name, register.code ? `(${register.code})` : null].filter(Boolean).join(' ');
        },
        cashSessionLabel() {
            const session = this.sale?.cash_register_session;
            if (!session) return '—';

            const openedAt = session.opened_at ? this.$format.datetime(session.opened_at) : 'sans ouverture';
            return `${this.statusLabel(session.status)} · ${openedAt}`;
        },
        deliveryLabel() {
            const delivery = this.sale?.delivery;
            if (!delivery) return '—';

            return `${delivery.reference || `#${delivery.id}`} · ${this.statusLabel(delivery.status)}`;
        },
        saleDocumentDialogTitle() {
            return this.saleDocumentPrintData?.document?.title
                ? `Aperçu ${this.saleDocumentPrintData.document.title.toLowerCase()}`
                : 'Aperçu document';
        },
    },
    mounted() {
        this.fetchSale();
    },
    methods: {
        canFinalizeDraftSale(sale) {
            if (!sale || sale.status !== 'draft') return false;
            if (this.canFinalizeClosedCashRegisterSale) return true;
            if (!this.isCaissier) return false;

            return !this.isSaleFromClosedCashRegisterSession(sale);
        },

        isSaleFromClosedCashRegisterSession(sale) {
            const status = sale?.cash_register_session?.status;
            return Boolean(status && status !== 'open');
        },

        async printTicket() {
            if (this.sale?.id) {
                const response = await this.$api.post(`/sales/${this.sale.id}/ticket-print`);
                this.sale = response.data?.data?.sale || this.sale;
                await nextTick();
            }
            this.printWithBodyClass('printing-sale-ticket');
        },

        async printSaleDocument(type) {
            if (!this.sale || this.isPreparingDocumentPrint) return;

            this.isPreparingDocumentPrint = true;
            this.pendingDocumentType = type;
            this.errorMessage = '';

            try {
                const response = await this.$api.get(`/sales/${this.sale.id}/document-data`, {
                    params: { type },
                });
                const documentData = response.data.data;
                const qrPayload = documentData.document?.qr_payload || '';

                this.saleDocumentQrCode = qrPayload
                    ? await QRCode.toDataURL(qrPayload, {
                        errorCorrectionLevel: 'M',
                        margin: 1,
                        width: 144,
                        color: {
                            dark: '#000000',
                            light: '#FFFFFF',
                        },
                    })
                    : '';
                this.saleDocumentPrintData = documentData;
                this.saleDocumentDialogVisible = true;

                await this.$nextTick();
                window.setTimeout(() => {
                    this.printPreparedSaleDocument();
                }, 150);
            } catch (error) {
                const errors = error.response?.data?.errors;
                this.errorMessage = errors
                    ? Object.values(errors).flat().join(' ')
                    : "Impossible de préparer l'impression du document.";
            } finally {
                this.isPreparingDocumentPrint = false;
                this.pendingDocumentType = '';
            }
        },

        printPreparedSaleDocument() {
            this.printWithBodyClass('printing-sale-document');
        },

        printWithBodyClass(className) {
            document.body.classList.add(className);
            const cleanup = () => {
                document.body.classList.remove(className);
                window.removeEventListener('afterprint', cleanup);
            };

            window.addEventListener('afterprint', cleanup, { once: true });
            window.print();
            window.setTimeout(cleanup, 1000);
        },

        async fetchSale() {
            this.isLoading = true;
            this.errorMessage = '';
            this.successMessage = '';
            const id = this.$route.params.id;

            try {
                const response = await this.$api.get(`/sales/${id}`);
                this.sale = response.data.data;
                this.sale.items = (this.sale.items || []).map((item) => ({
                    ...item,
                    cancelled_quantity: Number(item.cancelled_quantity || 0),
                    cancelled_amount: Number(item.cancelled_amount || 0),
                }));
            } catch (error) {
                this.errorMessage = 'Impossible de charger la vente.';
                this.sale = null;
            } finally {
                this.isLoading = false;
            }
        },

        statusLabel(status) {
            switch (status) {
                case 'draft':
                    return 'Brouillon';
                case 'validated':
                    return 'Validée';
                case 'cancelled':
                    return 'Annulée';
                case 'abandoned':
                    return 'Abandonnée';
                case 'open':
                    return 'Ouverte';
                case 'closed':
                    return 'Fermée';
                case 'pending':
                    return 'En attente';
                case 'assigned':
                    return 'Assignée';
                case 'delivered':
                    return 'Livrée';
                default:
                    return status;
            }
        },

        statusClass(status) {
            switch (status) {
                case 'draft':
                    return 'bg-secondary';
                case 'validated':
                    return 'bg-success';
                case 'cancelled':
                    return 'bg-danger';
                case 'abandoned':
                    return 'bg-secondary';
                case 'open':
                    return 'bg-success';
                case 'closed':
                    return 'bg-secondary';
                case 'pending':
                    return 'bg-warning text-dark';
                case 'assigned':
                    return 'bg-primary';
                case 'delivered':
                    return 'bg-info text-dark';
                default:
                    return 'bg-secondary';
            }
        },

        goBack() {
            this.$router.push({ name: 'Sales' });
        },

        formatDateTime(value) {
            return value ? this.$format.datetime(value) : '—';
        },

        userName(user) {
            return user?.name || '—';
        },

        paymentMethodLabel(method) {
            switch (method) {
                case 'cash':
                    return 'Espèces';
                case 'credit':
                    return 'Crédit';
                case 'mobile_money':
                    return 'Mobile money';
                case 'card':
                    return 'Carte bancaire';
                default:
                    return '—';
            }
        },

        lineSku(item) {
            return item.product?.sku || item.product_sku || 'Sans référence';
        },

        truncateProductName(value, limit = 52) {
            const productName = String(value || 'Produit sans nom').trim();

            if (productName.length <= limit) {
                return productName;
            }

            return `${productName.slice(0, Math.max(0, limit - 3)).trimEnd()}...`;
        },

        isPromotionLine(item) {
            return Number(item.base_unit_price || 0) > Number(item.unit_price || 0)
                || ['planned_promotion', 'cashier_promotion'].includes(String(item.price_source || ''));
        },

        isFormulaLine(item) {
            return String(item?.price_source || '') === 'formula';
        },

        coefficientLabel(value) {
            if (value === null || value === undefined || value === '') return '—';

            const coefficient = Number(value);
            return Number.isFinite(coefficient) ? `x${coefficient.toFixed(2)}` : '—';
        },

        coefficientBadgeClass(value) {
            const coefficient = Number(value);
            return Number.isFinite(coefficient) && coefficient <= 1 ? 'bg-danger' : 'bg-success';
        },

        remainingQuantity(item) {
            return Math.max(Number(item.quantity || 0) - Number(item.cancelled_quantity || 0), 0);
        },

        activeLineTotal(item) {
            const quantity = this.remainingQuantity(item);
            const formulaQuantity = Number(item.formula_quantity || 0);
            const formulaPrice = Number(item.formula_price || 0);

            if (this.isFormulaLine(item) && formulaQuantity >= 2 && formulaPrice > 0) {
                return (Math.floor(quantity / formulaQuantity) * formulaPrice)
                    + ((quantity % formulaQuantity) * Number(item.unit_price || 0));
            }

            return quantity * Number(item.unit_price || 0);
        },

        lineStatusLabel(item) {
            const cancelled = Number(item.cancelled_quantity || 0);
            if (cancelled <= 0) return 'Active';
            if (this.remainingQuantity(item) <= 0) return 'Annulée';
            return 'Partielle';
        },

        lineStatusClass(item) {
            const cancelled = Number(item.cancelled_quantity || 0);
            if (cancelled <= 0) return 'bg-success';
            if (this.remainingQuantity(item) <= 0) return 'bg-danger';
            return 'bg-warning text-dark';
        },

        canCancelLine(item) {
            return this.isGerant && this.sale?.status === 'validated' && this.remainingQuantity(item) > 0;
        },

        openCancelLineDialog(item) {
            this.selectedLine = item;
            this.cancelLineForm = {
                quantity: this.remainingQuantity(item),
                reason: '',
            };
            this.cancelLineDialogVisible = true;
        },

        closeCancelLineDialog() {
            if (this.isCancellingLine) return;
            this.cancelLineDialogVisible = false;
            this.selectedLine = null;
            this.cancelLineForm = { quantity: 1, reason: '' };
        },

        async submitCancelLine() {
            if (!this.sale || !this.selectedLine) return;
            const quantity = Number(this.cancelLineForm.quantity || 0);

            if (quantity <= 0 || quantity > this.remainingQuantity(this.selectedLine)) {
                this.errorMessage = 'Quantité à annuler invalide.';
                return;
            }

            this.isCancellingLine = true;
            this.errorMessage = '';
            this.successMessage = '';

            try {
                await this.$api.post(`/sales/${this.sale.id}/items/${this.selectedLine.id}/cancel`, {
                    quantity,
                    reason: this.cancelLineForm.reason || null,
                });
                this.successMessage = 'Produit annulé et stock réajusté.';
                this.cancelLineDialogVisible = false;
                this.selectedLine = null;
                this.cancelLineForm = { quantity: 1, reason: '' };
                await this.fetchSale();
            } catch (error) {
                const errors = error.response?.data?.errors;
                this.errorMessage = errors
                    ? Object.values(errors).flat().join(' ')
                    : "Une erreur est survenue lors de l'annulation du produit.";
            } finally {
                this.isCancellingLine = false;
            }
        },

        async validateCurrentSale() {
            if (!this.sale) return;

            const ok = await this.$confirmAction(
                `Valider la vente "${this.sale.reference}" ? Le stock sera mis à jour.`
            );
            if (!ok) return;

            try {
                await this.$api.post(`/sales/${this.sale.id}/validate`);
                this.successMessage = 'Vente validée avec succès.';
                await this.fetchSale();
            } catch (error) {
                const errors = error.response?.data?.errors;
                const msg = errors
                    ? Object.values(errors).flat().join(' ')
                    : "Une erreur est survenue lors de la validation de la vente.";
                this.errorMessage = msg;
            }
        },

        async cancelCurrentSale() {
            if (!this.sale) return;

            const ok = await this.$confirmAction(
                `Annuler la vente "${this.sale.reference}" ? Le stock sera réajusté.`
            );
            if (!ok) return;

            try {
                await this.$api.post(`/sales/${this.sale.id}/cancel`);
                this.successMessage = 'Vente annulée et stock réajusté.';
                await this.fetchSale();
            } catch (error) {
                const errors = error.response?.data?.errors;
                const msg = errors
                    ? Object.values(errors).flat().join(' ')
                    : "Une erreur est survenue lors de l'annulation de la vente.";
                this.errorMessage = msg;
            }
        },

        async deleteCurrentSale() {
            if (!this.sale) return;
            if (this.isDeleting) return;

            this.isDeleting = true;

            const ok = await this.$confirmAction(
                `Voulez-vous vraiment supprimer la vente "${this.sale.reference}" ?`
            );
            if (!ok) {
                this.isDeleting = false;
                return;
            }

            try {
                await this.$api.delete(`/sales/${this.sale.id}`);
                this.$router.push({ name: 'Sales' });
            } catch (error) {
                const errors = error.response?.data?.errors;
                const msg = errors
                    ? Object.values(errors).flat().join(' ')
                    : "Une erreur est survenue lors de la suppression de la vente.";
                this.errorMessage = msg;
            } finally {
                this.isDeleting = false;
            }
        },

    },
});
</script>

<style>
.sale-detail-shell {
  position: relative;
}

.sale-stat-card {
  background: #ffffff;
  border: 1px solid #e7eaf0;
  border-radius: 8px;
  box-shadow: 0 0.25rem 0.85rem rgba(15, 23, 42, 0.05);
  padding: 0.9rem 1rem;
}

.sale-lines-section {
  min-width: 0;
}

.sale-line-product-cell {
  max-width: 380px;
  min-width: 300px;
}

.sale-line-product {
  max-width: 100%;
  min-width: 0;
}

.sale-line-product-name {
  color: #212529;
  display: block;
  font-weight: 700;
  max-width: 52ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sale-line-product-sku {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sale-lines-summary {
  color: #212529;
  font-weight: 600;
}

.detail-row {
  align-items: flex-start;
  border-bottom: 1px solid #f1f3f5;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  padding: 0.45rem 0;
}

.detail-row:last-child {
  border-bottom: 0;
}

.detail-row span:first-child {
  color: #6c757d;
  flex: 0 0 42%;
  font-size: 0.85rem;
}

.detail-row strong {
  color: #212529;
  font-size: 0.9rem;
  text-align: right;
  word-break: break-word;
}

/* Styles spécifiques à l'impression */
@media print {
  body.printing-sale-ticket *,
  body.printing-sale-document * {
    visibility: hidden;
  }

  body.printing-sale-ticket #printable-ticket,
  body.printing-sale-ticket #printable-ticket *,
  body.printing-sale-document #printable-sale-document,
  body.printing-sale-document #printable-sale-document * {
    visibility: visible;
  }

  body.printing-sale-ticket #printable-ticket,
  body.printing-sale-document #printable-sale-document {
    position: absolute;
    left: 0;
    top: 0;
    margin: 0;
    padding: 0;
    width: 100%;
  }

  @page {
    margin: 5mm;
  }
}
</style>
